import { useState, useEffect, useCallback } from 'react';

/**
 * useActivityNotifications - Hook to track new/unread activities and notifications
 */
export default function useActivityNotifications() {
  const [notifications, setNotifications] = useState({});
  const [lastViewedTimes, setLastViewedTimes] = useState({});

  // Generate mock notification data based on groups and activities
  const generateNotifications = useCallback(() => {
    const groups = [
      'Soccer Team Families',
      'Neighborhood Parents', 
      'Art Class Friends',
      'STEM Parents Group'
    ];

    const mockNotifications = {};

    groups.forEach(groupName => {
      // Simulate some groups having new activity
      const hasNewActivity = Math.random() > 0.4; // 60% chance of new activity
      
      if (hasNewActivity) {
        const newActivities = Math.floor(Math.random() * 4) + 1; // 1-4 new activities
        const newPollResponses = Math.floor(Math.random() * 6); // 0-5 new poll responses
        const newMessages = Math.floor(Math.random() * 3); // 0-2 new messages
        
        mockNotifications[groupName] = {
          newActivities,
          newPollResponses,
          newMessages,
          lastActivity: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000), // Within last 24h
          hasUnread: newActivities > 0 || newPollResponses > 0 || newMessages > 0
        };
      } else {
        mockNotifications[groupName] = {
          newActivities: 0,
          newPollResponses: 0,
          newMessages: 0,
          lastActivity: null,
          hasUnread: false
        };
      }
    });

    return mockNotifications;
  }, []);

  // Initialize notifications
  useEffect(() => {
    const initialNotifications = generateNotifications();
    setNotifications(initialNotifications);
  }, [generateNotifications]);

  // Mark group activities as viewed
  const markGroupAsViewed = useCallback((groupName) => {
    setLastViewedTimes(prev => ({
      ...prev,
      [groupName]: new Date()
    }));

    setNotifications(prev => ({
      ...prev,
      [groupName]: {
        ...prev[groupName],
        newActivities: 0,
        newPollResponses: 0,
        newMessages: 0,
        hasUnread: false
      }
    }));
  }, []);

  // Get notification summary for a group
  const getGroupNotification = useCallback((groupName) => {
    const notification = notifications[groupName];
    if (!notification) return null;

    return {
      hasUnread: notification.hasUnread,
      totalNew: notification.newActivities + notification.newPollResponses + notification.newMessages,
      summary: generateSummaryText(notification),
      ...notification
    };
  }, [notifications]);

  // Generate summary text for notifications
  const generateSummaryText = (notification) => {
    const parts = [];
    
    if (notification.newActivities > 0) {
      parts.push(`${notification.newActivities} new activit${notification.newActivities === 1 ? 'y' : 'ies'}`);
    }
    
    if (notification.newPollResponses > 0) {
      parts.push(`${notification.newPollResponses} new poll response${notification.newPollResponses === 1 ? '' : 's'}`);
    }
    
    if (notification.newMessages > 0) {
      parts.push(`${notification.newMessages} new message${notification.newMessages === 1 ? '' : 's'}`);
    }

    return parts.join(' • ');
  };

  // Get total unread count across all groups
  const getTotalUnreadCount = useCallback(() => {
    return Object.values(notifications).reduce((total, notification) => {
      return total + (notification.hasUnread ? 1 : 0);
    }, 0);
  }, [notifications]);

  // Get total activity count across all groups
  const getTotalActivityCount = useCallback(() => {
    return Object.values(notifications).reduce((total, notification) => {
      return total + notification.newActivities + notification.newPollResponses + notification.newMessages;
    }, 0);
  }, [notifications]);

  // Simulate new activity arriving
  const simulateNewActivity = useCallback((groupName, activityType = 'activity') => {
    setNotifications(prev => {
      const current = prev[groupName] || {
        newActivities: 0,
        newPollResponses: 0,
        newMessages: 0,
        hasUnread: false
      };

      const updated = { ...current };
      
      switch (activityType) {
        case 'activity':
          updated.newActivities += 1;
          break;
        case 'poll':
          updated.newPollResponses += 1;
          break;
        case 'message':
          updated.newMessages += 1;
          break;
      }
      
      updated.hasUnread = true;
      updated.lastActivity = new Date();

      return {
        ...prev,
        [groupName]: updated
      };
    });
  }, []);

  return {
    notifications,
    getGroupNotification,
    markGroupAsViewed,
    getTotalUnreadCount,
    getTotalActivityCount,
    simulateNewActivity
  };
}
